import React, {useEffect, useState, useSyncExternalStore} from "react";
import {Navigate, Outlet, useLocation} from "react-router-dom";
import authSession from "../lib/authSession";
import {ROLE_HOME, loginUrlFor, safeNext} from "../lib/roles";

/**
 * How long to wait for the session check before rendering anyway. A hanging
 * refresh must never leave the user on a spinner forever.
 */
const BOOT_TIMEOUT_MS = 8000;

const AuthLoading = () => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-3">
      <div
        className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#16213A]"
        role="status"
        aria-label="Checking your session"
      />
      <p className="text-sm text-slate-500">Checking your session…</p>
    </div>
  </div>
);

/**
 * Route guard for the four authenticated areas.
 *
 * Policy (why it is not "one size fits all"):
 *   strict (admin, staff)  → not signed in ⇒ redirect to that area's login.
 *                            This is the behaviour those areas always had.
 *   soft   (learner, RP)   → not signed in but this browser has logged in
 *                            before ⇒ render the page; the API's 401s surface
 *                            as they did before the guard existed.
 *
 * It never evicts a working session: a failed refresh marks the session
 * `expired` (see authSession) and the page keeps rendering, and asking for one
 * area's session never clears another area's session.
 */
export default function RequireAuth({role, policy = "strict", children}) {
  const location = useLocation();
  const session = useSyncExternalStore(authSession.subscribe, authSession.getSnapshot);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    let settled = false;

    // Fast path: the session is already live for this area. Every in-app
    // navigation used to re-run ensureSession and briefly render the full-page
    // "Checking your session…" screen before the next page appeared. There is
    // nothing to restore here, so render immediately and let the global 401
    // refresh handle expiry in the background.
    if (authSession.isAuthenticatedFor(role)) {
      setReady(true);
      return () => {
        active = false;
      };
    }

    // Expired sessions, and soft areas with a remembered identity, render
    // anyway (the guard's own decision below is "render"). Do not hold the
    // route behind a refresh request: kick it off in the background and show
    // the page immediately. Otherwise a slow/failed refresh leaves every
    // sidebar click on the full-page spinner until the 8s timeout.
    const snapshot = authSession.getSnapshot();
    if (snapshot.status === "expired" || (policy === "soft" && authSession.hasLocalIdentity())) {
      setReady(true);
      authSession.ensureSession(role).catch(() => {});
      return () => {
        active = false;
      };
    }

    setReady(false);

    const finish = () => {
      if (active && !settled) {
        settled = true;
        setReady(true);
      }
    };
    const timer = setTimeout(finish, BOOT_TIMEOUT_MS);

    if (process.env.NODE_ENV !== "production" && authSession.isPublicPath(location.pathname)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[RequireAuth] "${location.pathname}" is listed as a public route in authSession but is wrapped in a guard — check App.js.`
      );
    }

    authSession.ensureSession(role).finally(() => {
      clearTimeout(timer);
      finish();
    });

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // Re-check on area entry and when the queried role changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, location.pathname]);

  if (!ready) return <AuthLoading />;

  const from = `${location.pathname}${location.search}`;
  const next = safeNext(from);
  const status = session.status;

  // Work out the decision first so it can be logged (dev) in one place.
  let decision;
  if (authSession.isAuthenticatedFor(role)) {
    decision = {type: "render"};
  } else if (status === "authenticated" && session.role && session.role !== role) {
    decision = {type: "home", to: ROLE_HOME[session.role] || "/"};
  } else if (status === "expired") {
    decision = {type: "render-expired"};
  } else if (policy === "soft" && authSession.hasLocalIdentity()) {
    decision = {type: "render-soft"};
  } else {
    decision = {type: "login", to: loginUrlFor(role, next)};
  }

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.info("[auth] guard", {
      path: location.pathname,
      role,
      policy,
      status,
      sessionRole: session.role,
      decision: decision.type,
      ...(decision.to ? {to: decision.to} : {}),
    });
  }

  if (decision.type === "render" || decision.type === "render-expired" || decision.type === "render-soft") {
    // A route element used as a pathless layout parent MUST render <Outlet />:
    // React Router provides the matched child through it, not as `children`
    // (returning `children` here rendered nothing — the blank page).
    return children ?? <Outlet />;
  }
  return <Navigate to={decision.to} replace state={{from: next}} />;
}

